import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactComponent as WarningOrangeIcon } from 'assets/error-orange.svg';
import { ReactComponent as WarningRedIcon } from 'assets/error-red.svg';
import { ReactComponent as VerticalDotsIcon } from 'assets/vertical-dots.svg';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { SortPopover } from 'components/shared/SortPopOver';
import { useAlertStatus } from 'customHook/useAlertStatus';
import dayjs from 'dayjs';
import { formatDealAmount } from 'helper/short-currency';
import { apiDealTypes, DealsTableColumns, DealType, PIPELINE_MAP } from 'model/deals.model';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { FilterState } from '../DealsFilters/DealsFilters.types';
import { filtersToQueryParams } from '../DealsFilters/DealsFilters.utils';
import { DealsSettings } from '../DealsSettings';
import { CustomPagination } from './Pagination';

const ClientAvatar = ({
  profilePicture,
  clientName,
}: {
  profilePicture?: string;
  clientName?: string;
}) => {
  const [imageError, setImageError] = useState(false);

  return (
    <Box
      sx={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: zeplinColor.Brand1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {profilePicture && !imageError ? (
        <img
          src={profilePicture}
          alt=''
          width='100%'
          height='100%'
          style={{ objectFit: 'cover' }}
          onError={() => setImageError(true)}
        />
      ) : clientName ? (
        <Typography
          variant='body2'
          sx={{
            color: zeplinColor.Neutral1,
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '24px',
          }}
        >
          {clientName.charAt(0).toUpperCase()}
        </Typography>
      ) : null}
    </Box>
  );
};

export interface Deal {
  title: string;
  stage: string;
  client: string;
  value: number;
  pipelineType: DealType | string;
  currentStep: string;
  pipelines: string[];
  owner: string;
  closeDate: Date;
  profilePicture: string;
  id?: string;
  dealId?: string;
  needsAttention?: 'warning' | 'onTrack' | 'overdue';
  dealHealth?: string;
  velocityTargetDate?: string | Date;
  [key: string]: any;
}

const SORTABLE_COLUMNS = ['dealName', 'stage', 'client', 'value', 'closeDate', 'owner'];

const SORT_LABELS: Record<string, { asc: string; desc: string }> = {
  stage: { asc: 'Start to End', desc: 'End to Start' },
  value: { asc: 'Lowest to Highest', desc: 'Highest to Lowest' },
  closeDate: { asc: 'Earliest to Latest', desc: 'Latest to Earliest' },
};

const SORT_FIELD_MAP: Record<string, string> = {
  dealName: 'dealName',
  stage: 'stage',
  client: 'clientName',
  value: 'dealValue',
  closeDate: 'closeDate',
  owner: 'ownerName',
};

interface DealsTableProps {
  filterState: FilterState;
  selectedPipelines: string[];
  tabValue: number;
  searchTerm?: string;
  needsAttention?: boolean;
  currentPage: number;
  handleSetCurrentPage: (number: number) => void;
}

export const DealsTable = ({
  filterState,
  selectedPipelines,
  tabValue,
  searchTerm = '',
  needsAttention = false,
  currentPage,
  handleSetCurrentPage,
}: DealsTableProps) => {
  const [sortAnchor, setSortAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: string; order: 'asc' | 'desc' }>({
    field: 'dealName',
    order: 'asc',
  });

  const { startOperation, completeOperation } = useAlertStatus();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const getDeals = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = filtersToQueryParams(
        filterState,
        tabValue,
        searchTerm,
        needsAttention,
        selectedPipelines,
      );
      queryParams.page = currentPage + 1;
      queryParams.limit = rowsPerPage;

      if (sortConfig.field) {
        const apiFieldName = SORT_FIELD_MAP[sortConfig.field] || sortConfig.field;
        queryParams.sort = `${apiFieldName}:${sortConfig.order}`;
      }

      const data = await DealsService.getDealsListView(queryParams);
      const mappedDeals = (data?.deals || data?.data || []).map((deal: any) => {
        let normalizedPipelineType = deal.pipelineType || deal.dealType || '';
        if (normalizedPipelineType && PIPELINE_MAP[normalizedPipelineType as apiDealTypes]) {
          normalizedPipelineType = PIPELINE_MAP[normalizedPipelineType as apiDealTypes];
        }

        const dealId = deal.id || deal.dealId || '';

        return {
          ...deal,
          title: deal.name || deal.title || '',
          stage: deal.dealStageName || deal.stage || deal.stageName || '',
          client: deal.clientName || deal.client || '',
          value: deal.dealValue || deal.amount || 0,
          pipelineType: normalizedPipelineType,
          currentStep: deal.stageName || deal.currentStep || '',
          pipelines: deal.pipelines || [],
          owner: deal.ownerName || '',
          closeDate: deal.closeDate || '',
          profilePicture: deal.profilePicture || deal.avatar || '',
          id: dealId,
          dealId: dealId,
          needsAttention: deal.needsAttention || 'onTrack',
          dealHealth: deal.dealHealth,
          dealHealthName: deal.dealHealthName,
          velocity: deal.velocity,
          velocityTargetDate: deal.velocityTargetDate,
          daysDiff: deal.daysDiff,
        };
      });
      setDeals(mappedDeals);
      setTotalCount(data?.total || data?.count || mappedDeals.length);
    } catch (error) {
      console.error('Failed to fetch deals', error);
      setDeals([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    filterState,
    selectedPipelines,
    tabValue,
    searchTerm,
    needsAttention,
    currentPage,
    rowsPerPage,
    sortConfig,
  ]);

  useEffect(() => {
    getDeals();
  }, [getDeals]);

  const [dealCardElement, setDealCardElement] = useState<SVGSVGElement | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);

  const handleCloseSettings = useCallback(() => {
    setDealCardElement(null);
    setSelectedDeal(null);
  }, []);

  const handleStageChange = useCallback(() => {
    getDeals();
  }, [getDeals]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      handleSetCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRowsChange = (newRows: number) => {
    setRowsPerPage(newRows);
    handleSetCurrentPage(0);
  };

  const handleOpenSettings = (event: MouseEvent<SVGSVGElement>, deal: any) => {
    event.stopPropagation();
    setDealCardElement(event.currentTarget);
    setSelectedDeal(deal);
  };

  if (loading) {
    return (
      <Box>
        <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
          <TableContainer
            sx={{
              overflow: 'auto',
              height: { xs: 'calc(100vh - 190px)', xl: 'calc(100vh - 450px)' },
            }}
          >
            <Table stickyHeader aria-label='sticky table'>
              <TableHead>
                <TableRow sx={headerRowStyles}>
                  {DealsTableColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      <Skeleton variant='text' width={100} height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => (
                  <TableRow key={index}>
                    {DealsTableColumns.map((column) => (
                      <TableCell key={column.id} align={column.align} sx={{ padding: '22px 12px' }}>
                        <Skeleton variant='text' width='80%' height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
        <TableContainer
          sx={{
            overflow: 'auto',
            height: { xs: 'calc(100vh - 450px)', xl: 'calc(100vh - 450px)' },
          }}
        >
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow sx={headerRowStyles}>
                {DealsTableColumns.map((column) => {
                  const isSortable = SORTABLE_COLUMNS.includes(column.id);

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      onClick={(e) =>
                        isSortable && setSortAnchor({ el: e.currentTarget, id: column.id })
                      }
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='body3'>{column.label}</Typography>
                        {isSortable && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            aria-label='button'
                          >
                            <UnfoldMoreIcon
                              sx={{
                                fontSize: 16,
                                color: zeplinColor.BasicBlack,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={DealsTableColumns.length} align='center'>
                    <Typography variant='body2'>Loading deals...</Typography>
                  </TableCell>
                </TableRow>
              ) : deals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={DealsTableColumns.length} align='center'>
                    <Typography variant='body2'>No deals found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                deals.map((row, ind) => {
                  const getRowColor = (): { borderColor: string; bgColor: string } => {
                    const attentionStatus = row.needsAttention
                      ? String(row.needsAttention).toLowerCase()
                      : null;
                    const health = row.dealHealth || row.dealHealthName;
                    const healthLower = health ? String(health).toLowerCase() : '';

                    if (attentionStatus === 'overdue' || healthLower === 'overdue') {
                      return { borderColor: zeplinColor.Error, bgColor: zeplinColor.BlockBG };
                    }

                    if (attentionStatus === 'warning' || healthLower === 'warning') {
                      return {
                        borderColor: zeplinColor.AlertInfo,
                        bgColor: zeplinColor.AlertInfoLight,
                      };
                    }

                    return { borderColor: zeplinColor.Neutral5, bgColor: 'none' };
                  };

                  const { borderColor, bgColor } = getRowColor();

                  return (
                    <TableRow
                      hover
                      key={ind}
                      component={Link}
                      to={`${RoutePaths.Deals}/${row.id}`}
                      sx={{
                        '&:last-child td, &:last-child th': {
                          borderBottom: `2px solid ${borderColor}`,
                        },
                        textDecoration: 'none',
                        color: 'inherit',
                        bgcolor: bgColor === 'none' ? 'transparent' : bgColor,
                        borderLeft: `2px solid ${borderColor}`,
                        cursor: 'pointer',
                      }}
                    >
                      {DealsTableColumns.map((column) => {
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            onClick={(e) => {
                              if (column.id === 'actions') {
                                e.stopPropagation();
                              }
                            }}
                            sx={{
                              padding: '22px 12px',
                              borderBottom: `1px solid ${borderColor}`,
                              borderTop: `1px solid ${borderColor}`,
                            }}
                          >
                            {renderCell(column.id, row, handleOpenSettings)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SortPopover
        anchorEl={sortAnchor?.el || null}
        setAnchorEl={() => setSortAnchor(null)}
        sortOption={sortConfig.field === sortAnchor?.id ? sortConfig.order : 'asc'}
        onSortChange={(order) => {
          if (sortAnchor) {
            setSortConfig({ field: sortAnchor.id, order });
            handleSetCurrentPage(0);
          }
          setSortAnchor(null);
        }}
        label={DealsTableColumns.find((c) => c.id === sortAnchor?.id)?.label || ''}
        ascLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.asc : undefined}
        descLabel={sortAnchor ? SORT_LABELS[sortAnchor.id]?.desc : undefined}
      />

      <DealsSettings
        anchorElement={dealCardElement}
        onClose={handleCloseSettings}
        startOperation={startOperation}
        completeOperation={completeOperation}
        deal={selectedDeal}
        onDealDeleted={handleStageChange}
        onStageChange={handleStageChange}
      />

      <CustomPagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsChange}
      />
    </Box>
  );
};

const headerRowStyles = {
  '& th': { backgroundColor: '#f5f5f5', borderBottom: 'none' },
  '& th:first-of-type': { borderRadius: '14px 0 0 14px' },
  '& th:last-of-type': { borderRadius: '0 14px 14px 0' },
};

const StagesColor = {
  early: {
    color: zeplinColor.Neutral8,
  },
  develop: {
    color: zeplinColor.AlertInfo,
  },
  active: {
    color: zeplinColor.Blue,
  },
  closing: {
    color: zeplinColor.Success,
  },
};

type CardDealType = undefined | 'warning' | 'error';

const renderCell = (
  columnId: string,
  row: Deal,
  handleOpenSettings: (event: MouseEvent<SVGSVGElement>, deal: any) => void,
) => {
  const attentionStatus = row.needsAttention ? String(row.needsAttention).toLowerCase() : null;
  const health = row.dealHealth || row.dealHealthName;
  const healthLower = health ? String(health).toLowerCase() : '';
  const isOverdue = attentionStatus === 'overdue' || healthLower === 'overdue';
  const isWarning = attentionStatus === 'warning' || healthLower === 'warning';
  const dateType: CardDealType = isOverdue ? 'error' : isWarning ? 'warning' : undefined;

  const renders: Record<string, () => JSX.Element | string> = {
    dealName: () => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '240px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '8px',
            height: '8px',
            borderRadius: '100%',
            flexShrink: 0,
            backgroundColor: isOverdue
              ? zeplinColor.Error
              : isWarning
                ? zeplinColor.AlertInfo
                : zeplinColor.Success,
          }}
        />
        <Typography noWrap sx={{ textOverflow: 'ellipsis' }}>
          {row.title}
        </Typography>
      </Box>
    ),
    dealsType: () => (
      <Chip
        label={
          <Typography variant='label1'>
            {row.pipelineType ? Utilities.toCamelCase(row.pipelineType) : 'N/A'}
          </Typography>
        }
        sx={{
          bgcolor: zeplinColor.BasicBlack,
          color: 'white',
          height: '22px',
          fontSize: '12px',
        }}
      />
    ),
    stage: () => {
      const stage = row.stage || '';
      const stageColor =
        StagesColor[stage as keyof typeof StagesColor]?.color || zeplinColor.Neutral4;
      return (
        <Chip
          label={
            <Typography variant='label1' sx={{ color: zeplinColor.Neutral1 }}>
              {stage ? Utilities.toCamelCase(stage) : 'N/A'}
            </Typography>
          }
          sx={{
            bgcolor: stageColor,
            height: '22px',
          }}
        />
      );
    },
    client: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ClientAvatar profilePicture={row.profilePicture} clientName={row.client} />
        <Typography variant='body2'>{row.client}</Typography>
      </Box>
    ),
    currentStep: () => (
      <Chip
        label={
          <Typography variant='label1'>
            {row.currentStep ? Utilities.toCamelCase(row.currentStep) : 'N/A'}
          </Typography>
        }
        sx={{ bgcolor: zeplinColor.Neutral4, height: 22 }}
      />
    ),
    closeDate: () => (
      <Date
        type={dateType}
        date={row.closeDate}
        daysDiff={row.daysDiff}
        velocityTargetDate={row.velocityTargetDate || row.velocity}
      />
    ),
    value: () => {
      return `${formatDealAmount(row.value)}`;
    },
    actions: () => (
      <Box
        sx={{
          padding: '3px',
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          ':hover': {
            backgroundColor: zeplinColor.Neutral5,
            cursor: 'pointer',
          },
          gap: '4px',
        }}
      >
        <VerticalDotsIcon
          style={{
            width: '18px',
            height: '18px',
          }}
          onClick={(e: MouseEvent<SVGSVGElement>) => {
            e.preventDefault();
            handleOpenSettings(e, row);
          }}
        />
      </Box>
    ),
  };

  return renders[columnId] ? renders[columnId]() : (row as any)[columnId];
};

const DealCardColors = {
  warning: zeplinColor.AlertInfo,
  error: zeplinColor.Error,
};

const Date = ({
  type,
  date,
  daysDiff,
  velocityTargetDate,
}: {
  type: CardDealType;
  date: Date | string;
  daysDiff?: number | string;
  velocityTargetDate?: number | string;
}) => {
  const formattedDate = dayjs(date).format('D MMM, YYYY');

  if (type === 'error') {
    const value =
      velocityTargetDate !== undefined && velocityTargetDate !== null
        ? Math.abs(
            typeof velocityTargetDate === 'string'
              ? parseFloat(velocityTargetDate)
              : velocityTargetDate,
          )
        : daysDiff !== undefined && daysDiff !== null
          ? Math.abs(typeof daysDiff === 'string' ? parseFloat(daysDiff) : daysDiff)
          : undefined;

    const toolTipText =
      value !== undefined
        ? `Deal exceeded velocity target by ${value} days`
        : 'Deal exceeded velocity target';

    return (
      <LightTooltip
        title={
          <Typography variant='body3' color={zeplinColor.Neutral1}>
            {toolTipText}
          </Typography>
        }
        placement='bottom-start'
        style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Typography variant='responsiveBody3' sx={{ color: DealCardColors.error }}>
            {formattedDate}
          </Typography>
          <WarningRedIcon />
        </Box>
      </LightTooltip>
    );
  }

  if (type === 'warning') {
    const value =
      velocityTargetDate !== undefined && velocityTargetDate !== null
        ? typeof velocityTargetDate === 'string'
          ? parseFloat(velocityTargetDate)
          : velocityTargetDate
        : daysDiff !== undefined && daysDiff !== null
          ? typeof daysDiff === 'string'
            ? parseFloat(daysDiff)
            : daysDiff
          : undefined;

    const toolTipText =
      value !== undefined
        ? `Deal approaching velocity target in ${value} days`
        : 'Deal approaching velocity target';

    return (
      <LightTooltip
        title={
          <Typography variant='body3' color={zeplinColor.Neutral1}>
            {toolTipText}
          </Typography>
        }
        placement='bottom-start'
        style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Typography variant='responsiveBody3' sx={{ color: DealCardColors.warning }}>
            {formattedDate}
          </Typography>
          <WarningOrangeIcon />
        </Box>
      </LightTooltip>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '4px' }}>
      <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
        {formattedDate}
      </Typography>
    </Box>
  );
};
